/**
 * Realtime as a dirty signal.
 *
 * Server realtime events are never merged into collections as data; they only
 * say "collection X changed". This scheduler turns those signals into
 * refetches of the collections a screen is currently showing, with two rules
 * that make it safe to fire at any rate:
 *
 * - **In view only.** A collection with no subscribers has no live query
 *   bound to it; its queryFn runs fresh when a screen next binds, so there is
 *   nothing to refresh. (`isReady()` alone is not enough: a collection stays
 *   "ready" for its gc window after the last screen unbinds, and the query
 *   layer has already detached its observers by then.) Subscribing to
 *   everything is therefore cheap — the work scales with what is on screen,
 *   not with what exists.
 * - **Coalesced.** Events within `debounceMs` collapse into one refetch, and
 *   an event that lands while a refetch is in flight marks it dirty so exactly
 *   one more runs afterwards. The result is always at least as new as the last
 *   event, and never more than one refetch is outstanding per collection.
 *
 * See docs/adr/0002-realtime-as-refetch-signal.md.
 */

export interface RefetchTarget {
	id: string;
	isReady(): boolean;
	readonly subscriberCount: number;
	utils: { refetch: () => Promise<unknown> };
}

/** A screen is showing this collection right now. */
export function isInView(target: RefetchTarget): boolean {
	return target.isReady() && target.subscriberCount > 0;
}

export interface RefetchScheduler {
	/** A change signal for `target`: refetch soon, if it is in view. */
	mark(target: RefetchTarget): void;
	/**
	 * Catch-up after a gap (reconnect, tab regains focus): refetch every
	 * in-view target now, skipping the debounce. Targets mid-refetch are
	 * marked dirty so they run once more with the post-gap state.
	 */
	flush(targets: Iterable<RefetchTarget>): void;
	/** Cancels pending timers; further marks are ignored. */
	dispose(): void;
}

interface TargetState {
	timer: ReturnType<typeof setTimeout> | null;
	inflight: Promise<void> | null;
	dirty: boolean;
}

export function createRefetchScheduler(opts: {
	debounceMs?: number;
	onError?: (targetId: string, error: unknown) => void;
}): RefetchScheduler {
	const debounceMs = opts.debounceMs ?? 250;
	const onError = opts.onError ?? (() => {});
	const states = new Map<string, TargetState>();
	let disposed = false;

	const stateFor = (target: RefetchTarget): TargetState => {
		let state = states.get(target.id);
		if (!state) {
			state = { timer: null, inflight: null, dirty: false };
			states.set(target.id, state);
		}
		return state;
	};

	const run = (target: RefetchTarget, state: TargetState): void => {
		if (disposed) return;
		if (state.inflight) {
			state.dirty = true;
			return;
		}
		// View can lapse between the signal and the timer (the last live
		// query unbinding); an unwatched collection refetches on its next bind.
		if (!isInView(target)) {
			state.dirty = false;
			return;
		}
		state.dirty = false;
		// Start the request synchronously so flush() really means "now".
		let request: Promise<unknown>;
		try {
			request = Promise.resolve(target.utils.refetch());
		} catch (error) {
			request = Promise.reject(error);
		}
		state.inflight = request
			.then(
				() => undefined,
				(error) => onError(target.id, error),
			)
			.finally(() => {
				state.inflight = null;
				if (state.dirty) run(target, state);
			});
	};

	const clearTimer = (state: TargetState) => {
		if (state.timer !== null) {
			clearTimeout(state.timer);
			state.timer = null;
		}
	};

	return {
		mark(target) {
			if (disposed || !isInView(target)) return;
			const state = stateFor(target);
			if (state.inflight) {
				state.dirty = true;
				return;
			}
			clearTimer(state);
			state.timer = setTimeout(() => {
				state.timer = null;
				run(target, state);
			}, debounceMs);
		},
		flush(targets) {
			if (disposed) return;
			for (const target of targets) {
				if (!isInView(target)) continue;
				const state = stateFor(target);
				clearTimer(state);
				run(target, state);
			}
		},
		dispose() {
			disposed = true;
			for (const state of states.values()) clearTimer(state);
			states.clear();
		},
	};
}
